import { useRef, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  UploadSimple,
  UserCircle,
  IdentificationCard,
  X,
  CheckCircle,
  WarningCircle,
  Info,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_SIZE = 10 * 1024 * 1024;
const ACCEPT = "image/png,image/jpeg,image/jpg,image/webp,image/gif";
const MAX_FILES = 8;

const bytes = (n) => (n < 1024 ? `${n} B` : n < 1048576 ? `${(n / 1024).toFixed(0)} KB` : `${(n / 1048576).toFixed(1)} MB`);

const UploadLane = ({ type, title, subtitle, testid, icon: Icon, tone, extraNote }) => {
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [handle, setHandle] = useState("");
  const [note, setNote] = useState("");
  const [status, setStatus] = useState({ state: "idle", msg: "" });
  const [dragging, setDragging] = useState(false);

  const addFiles = (list) => {
    const next = [...files];
    const errors = [];
    for (const f of Array.from(list)) {
      if (next.length >= MAX_FILES) { errors.push(`Max ${MAX_FILES} files`); break; }
      if (!f.type.startsWith("image/")) { errors.push(`${f.name}: images only`); continue; }
      if (f.size > MAX_SIZE) { errors.push(`${f.name}: over 10MB`); continue; }
      next.push(f);
    }
    setFiles(next);
    if (errors.length) setStatus({ state: "error", msg: errors[0] });
    else setStatus({ state: "idle", msg: "" });
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const removeAt = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (files.length === 0) {
      setStatus({ state: "error", msg: "Add at least one file" });
      return;
    }
    setStatus({ state: "loading", msg: "Uploading securely..." });
    try {
      const fd = new FormData();
      fd.append("submission_type", type);
      if (handle.trim()) fd.append("telegram_handle", handle.trim());
      if (note.trim()) fd.append("note", note.trim());
      files.forEach((f) => fd.append("files", f));
      await axios.post(`${API}/uploads`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setStatus({ state: "success", msg: "Received. I'll reach out on Telegram after review." });
      setFiles([]);
      setHandle("");
      setNote("");
      if (inputRef.current) inputRef.current.value = "";
    } catch (err) {
      const detail = err?.response?.data?.detail || "Upload failed. Please try again.";
      setStatus({ state: "error", msg: detail });
    }
  };

  const isRep = tone === "accent";

  return (
    <div
      data-testid={testid}
      className={`group border-r border-b border-border p-8 sm:p-10 flex flex-col relative ${
        isRep ? "bg-accent/[0.04]" : ""
      }`}
    >
      <div className={`absolute top-0 left-0 h-[2px] ${isRep ? "w-full" : "w-0 group-hover:w-full"} bg-accent transition-all duration-500`} />

      <div className="flex items-start justify-between mb-8">
        <Icon size={40} weight="duotone" className={isRep ? "text-accent" : "text-foreground"} />
        <span className={`font-code text-xs uppercase tracking-[0.2em] ${isRep ? "text-accent" : "text-muted-foreground"}`}>
          {isRep ? "For platform reps" : "For clients"}
        </span>
      </div>

      <h3 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight mb-3">
        {title}
      </h3>
      <p className="font-body text-muted-foreground leading-relaxed mb-6 max-w-md">
        {subtitle}
      </p>

      {extraNote && (
        <div className="flex items-start gap-3 border border-border bg-background/40 p-4 mb-6 max-w-md">
          <Info size={18} weight="duotone" className="text-accent shrink-0 mt-0.5" />
          <p className="font-code text-[11px] uppercase tracking-[0.12em] text-muted-foreground leading-relaxed">
            {extraNote}
          </p>
        </div>
      )}

      {/* Dropzone */}
      <label
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        data-testid={`${testid}-dropzone`}
        className={`block border border-dashed p-8 mb-5 cursor-pointer transition-colors duration-200 ${
          dragging ? "border-accent bg-accent/5" : "border-border hover:border-muted-foreground"
        }`}
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          multiple
          className="hidden"
          data-testid={`${testid}-input`}
          onChange={(e) => addFiles(e.target.files)}
        />
        <div className="flex flex-col items-center text-center gap-3">
          <UploadSimple size={28} weight="duotone" className="text-muted-foreground" />
          <div>
            <p className="font-body text-sm text-foreground">
              Drop images here or <span className="text-accent underline underline-offset-4">browse</span>
            </p>
            <p className="font-code text-[11px] uppercase tracking-[0.15em] text-muted-foreground mt-1">
              PNG · JPG · WEBP · Up to 10MB · Max {MAX_FILES} files
            </p>
          </div>
        </div>
      </label>

      {/* File chips */}
      {files.length > 0 && (
        <ul data-testid={`${testid}-file-list`} className="space-y-2 mb-5">
          {files.map((f, i) => (
            <li
              key={i}
              className="flex items-center justify-between gap-3 border border-border px-4 py-3 bg-background"
            >
              <span className="font-code text-xs text-foreground truncate">{f.name}</span>
              <div className="flex items-center gap-3 shrink-0">
                <span className="font-code text-[11px] text-muted-foreground">{bytes(f.size)}</span>
                <button
                  type="button"
                  onClick={() => removeAt(i)}
                  data-testid={`${testid}-remove-${i}`}
                  className="text-muted-foreground hover:text-foreground"
                  aria-label={`Remove ${f.name}`}
                >
                  <X size={14} />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Inputs */}
      <div className="grid grid-cols-1 gap-3 mb-5">
        <input
          type="text"
          placeholder="Your Telegram handle (optional) — e.g. @username"
          value={handle}
          onChange={(e) => setHandle(e.target.value)}
          data-testid={`${testid}-handle`}
          className="bg-background border border-border px-4 py-3 font-body text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent transition-colors"
        />
        <textarea
          rows={3}
          placeholder={isRep ? "Optional note (department, role, context)" : "Briefly describe your issue (optional)"}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          data-testid={`${testid}-note`}
          className="bg-background border border-border px-4 py-3 font-body text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-accent transition-colors resize-none"
        />
      </div>

      {/* Status */}
      {status.state !== "idle" && (
        <div
          data-testid={`${testid}-status`}
          className={`flex items-start gap-2 mb-5 font-code text-xs uppercase tracking-[0.12em] ${
            status.state === "success" ? "text-accent" : status.state === "error" ? "text-red-400" : "text-muted-foreground"
          }`}
        >
          {status.state === "success" && <CheckCircle size={16} weight="duotone" />}
          {status.state === "error" && <WarningCircle size={16} weight="duotone" />}
          {status.state === "loading" && <span className="h-4 w-4 border border-current border-t-transparent rounded-full animate-spin" />}
          <span className="normal-case tracking-normal">{status.msg}</span>
        </div>
      )}

      <button
        type="button"
        onClick={submit}
        disabled={status.state === "loading" || files.length === 0}
        data-testid={`${testid}-submit`}
        className={`mt-auto inline-flex items-center justify-center gap-3 px-7 py-4 font-code text-sm uppercase tracking-[0.15em] font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
          isRep
            ? "bg-accent text-accent-foreground hover:bg-blue-500"
            : "bg-foreground text-background hover:bg-accent hover:text-accent-foreground"
        }`}
      >
        {status.state === "loading" ? "Uploading..." : "Submit securely"}
      </button>
    </div>
  );
};

export const Uploads = () => {
  return (
    <section
      id="uploads"
      data-testid="uploads-section"
      className="border-t border-border bg-background"
    >
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-24 sm:py-36">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mb-14 max-w-3xl"
        >
          <span className="font-code text-xs uppercase tracking-[0.2em] text-accent">
            [ 06 ] — Secure file uploads
          </span>
          <h2 className="font-display font-bold tracking-tight leading-none text-4xl sm:text-5xl lg:text-6xl mt-5">
            Share your files securely.
          </h2>
          <p className="font-body text-muted-foreground mt-6 max-w-xl leading-relaxed">
            Upload directly from this page. Files are stored privately and only
            reviewed by me. All uploads are encrypted in transit.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 border-t border-l border-border">
          <UploadLane
            type="client"
            testid="upload-client"
            icon={UserCircle}
            title="Clients — share account screenshots"
            subtitle="Upload screenshots of your disabled account, error message, or restriction notice. The more context, the faster we can move."
            tone="default"
          />
          <UploadLane
            type="employee"
            testid="upload-employee"
            icon={IdentificationCard}
            title="Employees — verify your identity"
            subtitle="Upload a photo of your Meta / Google / platform ID card to unlock priority handling and compensation."
            extraNote="Fully optional — sharing your ID is not required. Please blur or cover any sensitive personal details before uploading. Only shown to Craniel."
            tone="accent"
          />
        </div>
      </div>
    </section>
  );
};

export default Uploads;
